/**
 * Vuex module for Guaranteed campaigns.
 *
 * This iteration is frontend-only on mocks — every action resolves
 * with hardcoded mock data after a short delay so the UI stays
 * exercised end-to-end without a backend. When the real API ships,
 * swap the mock helpers for actual `apiGuaranteed.*` calls.
 */

const MOCK_DELAY_MS = 400;

const mockDelay = (value) => new Promise((resolve) => {
	setTimeout(() => resolve(value), MOCK_DELAY_MS);
});

const buildMockCampaign = (id, overrides = {}) => ({
	id,
	external_id: `mock-${id}`,
	name: `Mock Guaranteed Campaign ${id}`,
	target_type: 'guaranteed',
	status: 1,
	start_date: '2026-08-18',
	end_date: '2026-08-28',
	device_count: 20,
	line_count: 1,
	plan_shows: 1200,
	fact_shows: 0,
	underdelivery_percent: 0,
	manager: 'Mock Manager',
	ctime: new Date().toISOString(),
	mtime: new Date().toISOString(),
	lines: [],
	...overrides,
});

const MOCK_LIST = [
	buildMockCampaign(1001, { name: 'Summer Campaign Kazan', status: 3 }),
	buildMockCampaign(1002, { name: 'Q3 Pyaterochka Boost', status: 1 }),
];

const MOCK_STATS = [
	{ date: '2026-08-18', plan_shows: 120, fact_shows: 118, cost: 4500, status: 'normal' },
	{ date: '2026-08-19', plan_shows: 120, fact_shows: 100, cost: 4000, status: 'underdelivery' },
];

const MOCK_PHOTO_REPORT = {
	campaign_id: 1001,
	intervals: [
		{ label: 'day', photos: [] },
		{ label: 'night', photos: [] },
	],
};


export default {
	namespaced: true,

	state: {
		list: Object.freeze([...MOCK_LIST]),
		stats: [],
		photoReport: null,
		operatorStats: null,
	},

	mutations: {
		setList(state, list) {
			state.list = Object.freeze(list);
		},
		addItem(state, item) {
			state.list = Object.freeze([...state.list, item]);
		},
		setStats(state, stats) {
			state.stats = Object.freeze(stats);
		},
		setPhotoReport(state, report) {
			state.photoReport = Object.freeze(report);
		},
		setOperatorStats(state, stats) {
			state.operatorStats = Object.freeze(stats);
		},
	},

	getters: {
		getById: (state) => (id) => state.list.find((c) => c.id === id) || null,
	},

	actions: {
		async requestList({ commit }) {
			const list = await mockDelay([...MOCK_LIST]);
			commit('setList', list);
			return list;
		},

		async getItem({ state, commit }, id) {
			const existing = state.list.find((c) => c.id === id);
			return mockDelay(existing || buildMockCampaign(id));
		},

		async create({ commit }, { data }) {
			const newId = Date.now();
			const created = {
				...buildMockCampaign(newId, { name: data.name || `Guaranteed ${newId}` }),
				...data,
				id: newId,
			};
			commit('addItem', created);
			return mockDelay(created);
		},

		async auditCampaign(_ctx, payload) {
			return mockDelay({ ok: true, payload });
		},

		async auditContent(_ctx, payload) {
			return mockDelay({ ok: true, payload });
		},

		async updateCampaignStatus(_ctx, payload) {
			return mockDelay({ ok: true, payload });
		},

		async requestStats({ commit }, _payload) {
			const stats = await mockDelay([...MOCK_STATS]);
			commit('setStats', stats);
			return stats;
		},

		async requestPhotoReport({ commit }, _payload) {
			const report = await mockDelay({ ...MOCK_PHOTO_REPORT });
			commit('setPhotoReport', report);
			return report;
		},

		async requestOperatorStats({ commit }, _payload) {
			const stats = await mockDelay({ total_cost: 0, total_plays: 0, total_devices: 0 });
			commit('setOperatorStats', stats);
			return stats;
		},
	},
};
